// App.tsx
import React, { useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Outlet,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";

import { StatusBar, Style } from "@capacitor/status-bar";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { dataLayer } from "@formstr/local-relay";

import { EventCreator } from "./components/EventCreator";
import { PollResponse } from "./components/PollResponse";
import { PollResults } from "./components/PollResults";
import Header from "./components/Header";
import { PrepareNote } from "./components/Notes/PrepareNote";
import { PrivateNote } from "./components/Notes/PrivateNote";

import { AppContextProvider } from "./contexts/app-context";
import { ListProvider } from "./contexts/lists-context";
import { UserProvider } from "./contexts/user-context";
import { RatingProvider } from "./contexts/RatingProvider";
import { ZapProvider } from "./contexts/ZapProvider";
import { MetadataProvider } from "./hooks/MetadataProvider";
import { NotificationProvider } from "./contexts/notification-context";
import { RelayProvider } from "./contexts/relay-context";
import { RelayHealthProvider } from "./contexts/RelayHealthContext";
import { NostrNotificationsProvider } from "./contexts/nostr-notification-context";
import { DMProvider } from "./contexts/dm-context";
import { ReportsProvider } from "./contexts/reports-context";
import { ModerationProvider } from "./contexts/moderation-context";
import { TranslationBatchProvider } from "./contexts/translation-batch-context";
import { FeedScrollProvider } from "./contexts/FeedScrollContext";
import { SubNavProvider } from "./contexts/SubNavContext";
import { HeaderTitleProvider } from "./contexts/HeaderTitleContext";
import { HeaderActionProvider } from "./contexts/HeaderActionContext";
import { ImmersiveScrollProvider } from "./contexts/ImmersiveScrollContext";
import CollapsibleHeader from "./components/Shell/CollapsibleHeader";
import { AppearanceProvider, useAppearance } from "./contexts/AppearanceContext";
import NavSidebar from "./components/SidePane";
import { VideoPlayerProvider } from "./contexts/VideoPlayerContext";
import { FloatingVideoPlayer } from "./components/Common/FloatingVideoPlayer";
import { useAndroidNotifications } from "./hooks/useAndroidNotifications";
import { UpdateBanner } from "./components/UpdateBanner";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, Box, useMediaQuery, useTheme } from "@mui/material";
import { buildTheme } from "./styles/theme";
import { getFontPreset } from "./styles/themes";

import EventList from "./components/Feed/FeedsLayout";
import NotesFeed from "./components/Feed/NotesFeed/components";
import HomeFeed from "./components/Feed/HomeFeed";
import ProfilesFeed from "./components/Feed/ProfileFeed";
import { PollFeed } from "./components/Feed/PollFeed";
import MoviesFeed from "./components/Feed/MoviesFeed";
import FollowPacksFeed from "./components/Feed/FollowPacksFeed";
import BookmarksFeed from "./components/Feed/BookmarksFeed";
import FollowPackDetail from "./components/FollowPacks/FollowPackDetail";
import FollowPackEditor from "./components/FollowPacks/FollowPackEditor";
import ArticlesFeed from "./components/Feed/ArticlesFeed";
import MusicFeed from "./components/Feed/MusicFeed";
import PlaylistDetail from "./components/Music/PlaylistDetail";
import SharedPlaylistDetail from "./components/Music/SharedPlaylistDetail";
import GamesFeed from "./components/Games/GamesFeed";
import Twenty48Board from "./games/twenty48/Board";
import TetrisBoard from "./games/tetris/Board";
import RacerBoard from "./games/racer/Board";
import { PlaybackProvider } from "./contexts/PlaybackContext";
import { PlaylistsProvider } from "./contexts/playlists-context";
import { DraftsProvider } from "./contexts/drafts-context";
import MiniPlayer from "./components/Music/MiniPlayer";
import ArticleDetail from "./components/Articles/ArticleDetail";
import MoviePage from "./components/Movies/MoviePage";
import { Nip89Provider } from "./contexts/Nip89Context";
import { useUserContext } from "./hooks/useUserContext";
import { useAppContext } from "./hooks/useAppContext";
import { DataLayerProvider } from "./dataLayer/hooks";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import { getDataLayer } from "@formstr/local-relay";
import TopicsFeed from "./components/Feed/TopicsFeed";
import TopicExplorer from "./components/Feed/TopicsFeed/TopicsExplorerFeed";
import FeedsLayout from "./components/Feed/FeedsLayout";
import ProfilePage from "./components/Profile/ProfilePage";
import ConversationList from "./components/Messages/ConversationList";
import ChatView from "./components/Messages/ChatView";
import NewConversation from "./components/Messages/NewConversation";
import NotificationsPage from "./components/Notifications/NotificationsPage";
import { SettingsScreen } from "./components/Settings/SettingsScreen";
import DraftsList from "./components/Drafts/DraftsList";
import NavDock, { useDockClearance } from "./components/Shell/NavDock";
import ExploreHub from "./components/Shell/ExploreHub";
import RightRail from "./components/Shell/RightRail";
import { useSectionColor } from "./components/Shell/useSectionColor";

/** Width of the desktop feed column (the block header spans just this). */
const CENTER_MAX_WIDTH = 680;

declare global {
  interface Window {
    nostr?: any;
  }
}

function AndroidNotifications() {
  useAndroidNotifications();
  return null;
}

// Reads appearance context and provides a dynamically built MUI theme
function DynamicThemeWrapper({ children }: { children: React.ReactNode }) {
  const { fontPresetId, colorPreset, finish } = useAppearance();
  const fontPreset = getFontPreset(fontPresetId);
  const theme = useMemo(
    () => buildTheme(fontPreset.fontFamily, colorPreset, finish),
    // Rebuild when the font, the selected preset, or (for the custom theme) the
    // chosen colors change — hence keying on the resolved color values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fontPreset.id,
      colorPreset.id,
      colorPreset.lightPrimary,
      colorPreset.darkPrimary,
      colorPreset.lightSecondary,
      colorPreset.darkSecondary,
      finish,
    ],
  );
  return (
    <ThemeProvider theme={theme} modeStorageKey="pollerama-color-scheme">
      {children}
    </ThemeProvider>
  );
}

// Feeds the current account's scope inputs (pubkey / follows / web-of-trust) to
// the data layer, so useEvents({ scope }) can resolve author-based feeds. Lives
// inside UserProvider; the worker itself is bootstrapped in index.tsx.
function DataLayerScopeBridge({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const scopeUser = React.useMemo(
    () => ({ pubkey: user?.pubkey, follows: user?.follows, webOfTrust: user?.webOfTrust }),
    [user?.pubkey, user?.follows, user?.webOfTrust]
  );
  return (
    <DataLayerProvider user={scopeUser} dataLayer={getDataLayer()}>
      {children}
    </DataLayerProvider>
  );
}

// Inner component: static layout — header on top, sidebar + content below
function AppContent() {
  const [sidebarOpen, setSidebarOpen] = React.useState(
    () => localStorage.getItem("pollerama:sidebarOpen") !== "false"
  );
  const toggleSidebar = () =>
    setSidebarOpen((prev) => {
      localStorage.setItem("pollerama:sidebarOpen", String(!prev));
      return !prev;
    });

  const { user } = useUserContext();
  const { resetStore } = useAppContext();
  const location = useLocation();
  const muiTheme = useTheme();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up("md"));
  const isWide = useMediaQuery(muiTheme.breakpoints.up("lg"));
  const section = useSectionColor();
  const prevPubkeyRef = React.useRef<string | null | undefined>(undefined);

  React.useEffect(() => {
    const prev = prevPubkeyRef.current;
    const next = user?.pubkey ?? null;
    // undefined = first render (skip); null→pubkey or pubkey→pubkey = actual switch
    if (prev !== undefined && prev !== next) {
      resetStore();
      // Account switch: the DataLayerProvider's user/scope change drives the
      // worker's re-sync; resume() just nudges it that the foreground is active.
      dataLayer.resume();
    }
    prevPubkeyRef.current = next;
  }, [user?.pubkey, resetStore]);

  const routes = (
          <ErrorBoundary resetKey={location.pathname}>
          <Routes>
          {/* The composer owns its scroll area so the tool/Post dock can sit
              below it (above the keyboard) without covering the fields. */}
          <Route path="/create" element={<EventCreator />} />
          <Route
            path="/respond/:eventId"
            element={<ByParams><ScrollPage><PollResponse /></ScrollPage></ByParams>}
          />
          <Route
            path="note/:eventId"
            element={<ByParams><PrepareNoteWrapper /></ByParams>}
          />
          <Route
            path="p/:nevent"
            element={<ByParams><PrivateNoteWrapper /></ByParams>}
          />
          <Route
            path="/profile/:npubOrNprofile"
            element={<ByParams><ProfilePage /></ByParams>}
          />
          <Route
            path="/result/:eventId"
            element={<ByParams><ScrollPage><PollResults /></ScrollPage></ByParams>}
          />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/drafts" element={<ScrollPage><DraftsList /></ScrollPage>} />
          <Route path="/notifications" element={<ScrollPage><NotificationsPage /></ScrollPage>} />
          <Route path="/messages" element={<ScrollPage><ConversationList /></ScrollPage>} />
          <Route path="/messages/new" element={<ScrollPage><NewConversation /></ScrollPage>} />
          <Route path="/messages/:npub" element={<ByParams><ChatView /></ByParams>} />
          <Route path="/ratings" element={<EventList />} />

          <Route path="/feeds" element={<FeedsLayout />}>
            <Route path="home" element={<HomeFeed />} />
            <Route path="notes" element={<NotesFeed />} />
            <Route path="profiles" element={<ProfilesFeed />} />
            <Route path="topics" element={<TopicsFeed />}>
              <Route path=":tag" element={<ByParams><TopicExplorer /></ByParams>} />
            </Route>
            <Route path="polls" element={<PollFeed />} />
            <Route path="follow-packs" element={<FollowPacksFeed />} />
            <Route path="follow-packs/new" element={<FollowPackEditor />} />
            <Route path="follow-packs/:naddr" element={<ByParams><FollowPackDetail /></ByParams>} />
            <Route path="follow-packs/:naddr/edit" element={<ByParams><FollowPackEditor /></ByParams>} />
            <Route path="bookmarks" element={<BookmarksFeed />} />
            <Route path="articles" element={<ArticlesFeed />} />
            <Route path="articles/:naddr" element={<ByParams><ArticleDetail /></ByParams>} />
            <Route path="music" element={<MusicFeed />} />
            <Route path="music/shared/:naddr" element={<ByParams><SharedPlaylistDetail /></ByParams>} />
            <Route path="music/:playlistId" element={<ByParams><PlaylistDetail /></ByParams>} />
            <Route path="explore" element={<ExploreHub />} />
            <Route path="games" element={<GamesFeed />} />
            <Route path="games/2048" element={<Twenty48Board />} />
            <Route path="games/tetris" element={<TetrisBoard />} />
            <Route path="games/racer" element={<RacerBoard />} />

            <Route element={<Outlet />}>
              <Route path="movies" element={<MoviesFeed />} />
              <Route
                path="movies/:imdbId"
                element={<ByParams><MoviePage /></ByParams>}
              />
            </Route>

            <Route index element={<PollFeed />} />
          </Route>

          <Route
            index
            path="/"
            element={<Navigate to={`/feeds/${localStorage.getItem("pollerama:lastFeed") || "home"}`} replace />}
          />
        </Routes>
          </ErrorBoundary>
  );

  // Desktop: a centered frame — ink rail, the feed column (with the block header
  // over it only), and a discovery column on wide screens.
  if (isDesktop) {
    return (
      <Box sx={{ height: "100%", overflow: "hidden", display: "flex", justifyContent: "center", gap: 2, px: 2 }}>
        <NavSidebar open={sidebarOpen} onToggle={toggleSidebar} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: CENTER_MAX_WIDTH,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <CollapsibleHeader>
            <Header />
          </CollapsibleHeader>
          <UpdateBanner />
          {/* position:relative anchors the feed-actions button to this column. */}
          <Box key={user?.pubkey ?? 'anon'} sx={{ flex: 1, minHeight: 0, overflow: "hidden", position: "relative" }}>
            {routes}
          </Box>
          <MiniPlayer />
        </Box>
        {isWide && <RightRail />}
      </Box>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* The status-bar inset is painted with the block color so the header
          block runs edge to edge under the system bar. */}
      <Box
        className="header-safe-area"
        sx={{
          // Only the status-bar inset is painted (in the section's color), so the
          // header's rounded bottom corners show the page behind them.
          background: `linear-gradient(${section.color}, ${section.color}) top / 100% calc(env(safe-area-inset-top) + 1px) no-repeat`,
        }}
      >
        <CollapsibleHeader>
          <Header />
        </CollapsibleHeader>
      </Box>
      <UpdateBanner />
      <Box key={user?.pubkey ?? 'anon'} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {routes}
      </Box>
      {/* Docked at the bottom of the app column: it shrinks the content above
          rather than overlaying it, so it never hides feed items. */}
      <MiniPlayer />
      <NavDock />
    </div>
  );
}

const App: React.FC = () => {
  // ⚡ Capacitor status bar setup
  //
  // Edge-to-edge: the app draws under the system bars and pads its own chrome via
  // env(safe-area-inset-*) (see .header-safe-area + the body rule in index.css).
  // Insetting the WebView here (overlay:false) is what made the app read as a web
  // page in a frame rather than a native app.
  useEffect(() => {
    const apply = async (dark: boolean) => {
      try {
        await StatusBar.setOverlaysWebView({ overlay: true });
        // Style.Dark = light icons (for dark backgrounds); Style.Light = dark icons.
        await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
      } catch (e) {
        console.warn("StatusBar plugin error:", e);
      }
    };

    // The theme defaults to the system scheme (ThemeProvider modeStorageKey with
    // no explicit mode), so the media query tracks what the app is actually showing.
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    apply(mq.matches);
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Tell the worker the app returned to the foreground. The worker owns the
  // WebSocket connections (killed by the OS when backgrounded, especially on
  // mobile/Capacitor) and decides how to recover — the app only signals the
  // foreground transition it can observe but the worker can't. Event pruning is
  // also the worker's responsibility now (its PrunePolicy), so no app-side prune.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Native: Capacitor fires appStateChange reliably on Android/iOS
      let listener: Awaited<ReturnType<typeof CapApp.addListener>> | null = null;
      CapApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) dataLayer.resume();
      }).then((l) => { listener = l; });
      // Also handle network coming back online (e.g. WiFi → cellular switch)
      const onOnline = () => dataLayer.resume();
      window.addEventListener("online", onOnline);
      return () => {
        listener?.remove();
        window.removeEventListener("online", onOnline);
      };
    } else {
      // Web: visibilitychange is reliable; resume whenever tab becomes visible
      const onVisibilityChange = () => {
        if (!document.hidden) dataLayer.resume();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("online", () => dataLayer.resume());
      return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  }, []);

  return (
    <NotificationProvider>
      <AppearanceProvider>
        <DynamicThemeWrapper>
          <AppContextProvider>
            <UserProvider>
              <ModerationProvider>
              <DataLayerScopeBridge>
              <RelayProvider>
                <RelayHealthProvider>
                <DMProvider>
                <NostrNotificationsProvider>
                  <TranslationBatchProvider>
                    <ListProvider>
                      <ZapProvider>
                      <RatingProvider>
                        <Nip89Provider>
                        <ReportsProvider>
                        <CssBaseline />
                        <MetadataProvider>
                          <VideoPlayerProvider>
                            <PlaybackProvider>
                            <PlaylistsProvider>
                            <DraftsProvider>
                            <Router>
                              <AndroidNotifications />
                              <FeedScrollProvider>
                                <SubNavProvider>
                                  <HeaderTitleProvider>
                                    <HeaderActionProvider>
                                      <ImmersiveScrollProvider>
                                        <AppContent />
                                      </ImmersiveScrollProvider>
                                    </HeaderActionProvider>
                                  </HeaderTitleProvider>
                                </SubNavProvider>
                              </FeedScrollProvider>
                              <FloatingVideoPlayer />
                            </Router>
                            </DraftsProvider>
                            </PlaylistsProvider>
                            </PlaybackProvider>
                          </VideoPlayerProvider>
                        </MetadataProvider>
                        </ReportsProvider>
                        </Nip89Provider>
                      </RatingProvider>
                      </ZapProvider>
                    </ListProvider>
                  </TranslationBatchProvider>
                </NostrNotificationsProvider>
                </DMProvider>
                </RelayHealthProvider>
              </RelayProvider>
              </DataLayerScopeBridge>
              </ModerationProvider>
            </UserProvider>
          </AppContextProvider>
        </DynamicThemeWrapper>
      </AppearanceProvider>
    </NotificationProvider>
  );
};

/**
 * Remount a page when its URL parameters change. React Router reuses the mounted
 * element when only a param changes (topic → topic, pack → pack, profile →
 * profile), so pages that accumulate state — loaded notes, seen-ids, cursors,
 * moderation maps — kept showing the previous item's content under the new
 * title. A fresh mount per param also closes the old subscription before it can
 * deliver late events into the new page.
 */
function ByParams({ children }: { children: React.ReactNode }) {
  const params = useParams();
  return <React.Fragment key={JSON.stringify(params)}>{children}</React.Fragment>;
}

// Standalone pages need their own overflow-y:auto container because the global
// layout locks html/body overflow so Virtuoso can be the sole scroller on feeds.
// paddingBottom reserves space for the floating mobile nav dock.
function ScrollPage({ children }: { children: React.ReactNode }) {
  const dockClearance = useDockClearance();
  return (
    <Box sx={{ height: "100%", overflowY: "auto", ...dockClearance }}>
      {children}
    </Box>
  );
}

// Wrapper to pass eventId to PrepareNote.
function PrepareNoteWrapper() {
  const { eventId } = useParams();
  const dockClearance = useDockClearance();
  if (!eventId) return null;
  return (
    <Box sx={{ height: "100%", overflowY: "auto", ...dockClearance }}>
      <PrepareNote neventId={eventId} />
    </Box>
  );
}

// Wrapper for the private-note reader. ViewKey lives in the URL fragment so
// it never leaves the browser.
function PrivateNoteWrapper() {
  const { nevent } = useParams();
  const dockClearance = useDockClearance();
  if (!nevent) return null;
  return (
    <Box sx={{ height: "100%", overflowY: "auto", ...dockClearance }}>
      <PrivateNote neventId={nevent} />
    </Box>
  );
}

export default App;
